import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Image from 'next/image';

import VideoIcon from '../VideoIcon';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import s from './InsightsSectionHeading.module.scss';

const TEXT_COLOR_LIGHT = 'text-light';
const TEXT_COLOR_DARK = 'text-dark';

const THEME_PINK_PURPLE_BLUE = 'pink-purple-blue';
const THEME_PINK_PURPLE = 'pink-purple';
const THEME_PINK_ORANGE = 'pink-orange';
const THEME_BLUE_PURPLE = 'blue-purple';
const THEME_BLUE_GREEN = 'blue-green';
const THEME_WHITE = 'white';

const ICON_CLOUDS = 'clouds';
const ICON_CLOUD = 'cloud';

const VIDEO_ICON_BALLOON = 'balloon';

const videoIconMap = {
  [VIDEO_ICON_BALLOON]: [
    {
      src: '/video/balloon--600p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/balloon--600p--vp9.webm',
      type: 'video/webm',
    },
  ],
};

const InsightsSectionHeading = (props) => {
  const {
    label,
    boldTitle,
    regularTitle,
    preamble,
    textColor,
    titleTheme,
    iconTheme,
    leftIcon,
    rightIcon,
    videoIcon,
    modifiers,
  } = props;

  const [inViewRef, isVisible] = useIntersectionObserver(0.5);

  const rootCls = classNames(
    s.Root,
    {
      [s.ShortRightIconTransitionDelay]:
        (!!rightIcon || !!videoIcon) && !!!leftIcon,

      [s.TextColorDark]: textColor === TEXT_COLOR_DARK,

      [s.TitleThemePinkPurple]: titleTheme === THEME_PINK_PURPLE,
      [s.TitleThemePinkOrange]: titleTheme === THEME_PINK_ORANGE,
      [s.TitleThemeBluePurple]: titleTheme === THEME_BLUE_PURPLE,
      [s.TitleThemeBlueGreen]: titleTheme === THEME_BLUE_GREEN,

      [s.Visible]: isVisible,
    },
    modifiers.map((m) => s[m])
  );

  const videoIconSrcs =
    videoIcon && videoIcon in videoIconMap ? videoIconMap[videoIcon] : null;

  return (
    <div className={rootCls}>
      <div className={`${s.IconContainer} grid`}>
        {leftIcon && (
          <div className={s.IconLeftWrapper}>
            <Image
              width={600}
              height={1}
              quality={100}
              src={`/img/${leftIcon}--${iconTheme}.webp`}
              alt=""
              className={s.IconLeft}
            />
          </div>
        )}

        {videoIconSrcs && (
          <div className={s.VideoIconRightWrapper}>
            <VideoIcon
              className={s.VideoIcon}
              active={isVisible}
              loop={true}
              srcs={videoIconSrcs}
            />
          </div>
        )}

        {rightIcon && !videoIconSrcs && (
          <div className={s.IconRightWrapper}>
            <Image
              width={650}
              height={1}
              quality={100}
              src={`/img/${rightIcon}--${iconTheme}.webp`}
              alt=""
              className={s.IconRight}
            />
          </div>
        )}
      </div>
      <div ref={inViewRef} className={s.Container}>
        {videoIconSrcs && (
          <div className={s.ResponsiveVideoIconWrapper}>
            <VideoIcon
              className={s.ResponsiveVideoIcon}
              active={isVisible}
              loop={true}
              srcs={videoIconSrcs}
            />
          </div>
        )}

        {label && (
          <div className={`grid`}>
            <span className={s.TitleLabel}>{label}</span>
          </div>
        )}

        <div className={`${s.TitleRespWrap} grid`}>
          <h3 className={`${s.TitleWrap} grid`}>
            <span className={s.TitleTop}>{boldTitle}</span>&nbsp;&nbsp;
            <span className={s.TitleBottom}>{regularTitle}</span>
          </h3>
        </div>

        {preamble && (
          <div className="grid">
            <p className={s.Text}>{preamble}</p>
          </div>
        )}
      </div>
    </div>
  );
};

InsightsSectionHeading.propTypes = {
  label: PropTypes.string,
  boldTitle: PropTypes.string.isRequired,
  regularTitle: PropTypes.string.isRequired,
  preamble: PropTypes.string,
  textColor: PropTypes.oneOf([TEXT_COLOR_DARK, TEXT_COLOR_LIGHT]),
  titleTheme: PropTypes.oneOf([
    THEME_PINK_PURPLE,
    THEME_PINK_ORANGE,
    THEME_BLUE_PURPLE,
    THEME_BLUE_GREEN,
  ]),
  iconTheme: PropTypes.oneOf([
    THEME_PINK_PURPLE_BLUE,
    THEME_PINK_PURPLE,
    THEME_PINK_ORANGE,
    THEME_BLUE_PURPLE,
    THEME_BLUE_GREEN,
    THEME_WHITE,
  ]),
  leftIcon: PropTypes.oneOf([ICON_CLOUD, ICON_CLOUDS]),
  rightIcon: PropTypes.oneOf([ICON_CLOUD, ICON_CLOUDS]),
  videoIcon: PropTypes.oneOf([VIDEO_ICON_BALLOON]),
  modifiers: PropTypes.arrayOf(PropTypes.string),
};

InsightsSectionHeading.defaultProps = {
  boldTitle: '',
  regularTitle: '',
  preamble: '',
  titleTheme: null,
  iconTheme: THEME_WHITE,
  leftIcon: null,
  rightIcon: null,
  videoIcon: null,
  modifiers: [],
};

export default InsightsSectionHeading;
